/**
 * Los hooks de datos del import de listas.
 *
 * POR QUE VIVEN ACA Y NO EN `lib/queries.ts`
 * ------------------------------------------
 * `lib/queries.ts` no tiene hooks de listas y no es de este trabajo tocarlo. Se
 * arman locales contra `api` de `lib/api.ts`, que sigue siendo la unica puerta
 * al backend: nada de `fetch` suelto, salvo la subida, que no puede pasar por
 * ahi y esta explicada mas abajo.
 *
 * Las claves de cache usan el prefijo `["listas", ...]`, que no colisiona con
 * ninguna familia existente. Aplicar o revertir SI invalida familias ajenas —
 * productos, stock y precios— y eso no es opcional: `aplicar_lista_import()`
 * reescribe costos y el trigger recalcula `precio_venta` de cada producto
 * tocado. Cualquier precio cacheado despues de aplicar es un precio que ya no
 * existe, y ese es el numero que el vendedor le dice al cliente en voz alta.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, desenvolver, ErrorApi, URL_API } from "@/lib/api";
import { claves } from "@/lib/queries";

import type {
  AccionFila,
  EstadoImport,
  FilaRevision,
  ImportLista,
  ResultadoAplicar,
  ResultadoRevertir,
  ResumenMatching,
} from "./logica";

/** Un PDF de 40 paginas no se lee en 15 segundos. */
const TIMEOUT_LARGO = 60_000;

/** Cada cuanto se pregunta si el import termino, mientras esta trabajando. */
const INTERVALO_POLLING = 1_500;

export const clavesListas = {
  raiz: ["listas"] as const,
  import: (id: string) => ["listas", "import", id] as const,
  revision: (id: string) => ["listas", "revision", id] as const,
  recientes: ["listas", "recientes"] as const,
};

export interface RespuestaImport {
  import: ImportLista;
  filas: unknown[];
  resumen: ResumenMatching | null;
}

export interface RespuestaRevision {
  import: ImportLista;
  filas: FilaRevision[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Subida
// ─────────────────────────────────────────────────────────────────────────────

export interface DatosSubida {
  archivo: File;
  proveedorId: number | null;
  /** Porcentaje de bytes ya enviados, 0 a 100. */
  onProgreso?: (pct: number) => void;
}

export interface RespuestaSubida {
  import_id: string;
  estado: EstadoImport;
}

function leerCuerpo(texto: string): unknown {
  if (texto === "") return null;
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return texto;
  }
}

function mensajeDeCuerpo(cuerpo: unknown, estado: number): string {
  if (typeof cuerpo === "object" && cuerpo !== null) {
    const error = (cuerpo as { error?: unknown }).error;
    if (typeof error === "string" && error.trim() !== "") return error;
  }
  if (typeof cuerpo === "string" && cuerpo.trim() !== "") return cuerpo;
  return `El servidor respondió ${estado} sin explicación.`;
}

/**
 * Sube el archivo y arranca la extraccion.
 *
 * POR QUE NO USA `api.post`
 * -------------------------
 * `pedir()` serializa el cuerpo con `JSON.stringify` y este endpoint exige
 * `multipart/form-data`. Ademas —y esto es lo que decide— `fetch` no informa el
 * progreso de subida: no hay evento mientras los bytes viajan. Con un limite de
 * 24 MB y una lista escaneada de un proveedor, eso son varios segundos de
 * pantalla muda. `XMLHttpRequest` si da `upload.progress`, y ese porcentaje es
 * el unico numero honesto de todo el proceso.
 *
 * Lo demas se respeta igual que en `api.ts`: `withCredentials` para que viaje la
 * cookie httpOnly de sesion, y el mensaje del backend intacto en el error.
 */
export function subirLista({
  archivo,
  proveedorId,
  onProgreso,
}: DatosSubida): Promise<RespuestaSubida> {
  return new Promise((resolver, rechazar) => {
    const cuerpo = new FormData();
    cuerpo.append("archivo", archivo);
    if (proveedorId !== null) cuerpo.append("proveedor_id", String(proveedorId));

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${URL_API}/listas/import`);
    xhr.withCredentials = true;

    if (onProgreso) {
      xhr.upload.addEventListener("progress", (evento) => {
        if (evento.lengthComputable && evento.total > 0) {
          onProgreso(Math.min(100, Math.round((evento.loaded / evento.total) * 100)));
        }
      });
    }

    xhr.addEventListener("load", () => {
      const contenido = leerCuerpo(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        const datos = desenvolver<RespuestaSubida>(contenido);
        if (datos && typeof datos.import_id === "string") {
          resolver(datos);
          return;
        }
        rechazar(
          new ErrorApi({
            mensaje: "El servidor aceptó el archivo pero no devolvió el import.",
            codigo: "RESPUESTA_INESPERADA",
            estado: xhr.status,
            cuerpo: contenido,
          }),
        );
        return;
      }
      rechazar(
        new ErrorApi({
          mensaje: mensajeDeCuerpo(contenido, xhr.status),
          estado: xhr.status,
          cuerpo: contenido,
        }),
      );
    });

    xhr.addEventListener("error", () => {
      rechazar(
        new ErrorApi({
          mensaje: "No pudimos conectarnos con el servidor. Revisá que el sistema esté corriendo.",
          codigo: "SIN_CONEXION",
          estado: 0,
        }),
      );
    });

    xhr.addEventListener("abort", () => {
      rechazar(new ErrorApi({ mensaje: "Se canceló la subida.", codigo: "CANCELADO", estado: 0 }));
    });

    xhr.send(cuerpo);
  });
}

export function useSubirLista(): UseMutationResult<RespuestaSubida, ErrorApi, DatosSubida> {
  return useMutation<RespuestaSubida, ErrorApi, DatosSubida>({ mutationFn: subirLista });
}

// ─────────────────────────────────────────────────────────────────────────────
// Lectura
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Estado del import. Se repregunta sola mientras el backend trabaja.
 *
 * El polling se apaga en cuanto el estado deja de ser PENDIENTE o PROCESANDO:
 * un import COMPLETADO no cambia solo, y seguir preguntando cada segundo y medio
 * mientras alguien revisa 400 filas es ruido contra el servidor.
 */
export function useImportLista(id: string | null): UseQueryResult<RespuestaImport, ErrorApi> {
  return useQuery<RespuestaImport, ErrorApi>({
    queryKey: clavesListas.import(id ?? ""),
    queryFn: ({ signal }) =>
      api.get<RespuestaImport>(`/listas/import/${id ?? ""}`, { signal, timeout: TIMEOUT_LARGO }),
    enabled: id !== null,
    refetchInterval: (consulta) => {
      const estado = consulta.state.data?.import.estado;
      return estado === "PENDIENTE" || estado === "PROCESANDO" ? INTERVALO_POLLING : false;
    },
  });
}

/**
 * Las ultimas listas leidas (sin aplicar) y aplicadas, de cualquier compu.
 *
 * Antes el unico camino de vuelta a una lista era el id guardado en ESTE
 * navegador: desde otra compu, una lista leida y sin aplicar no aparecia, y
 * una aplicada perdia su boton de Revertir.
 */
export function useImportsRecientes(): UseQueryResult<ImportLista[], ErrorApi> {
  return useQuery<ImportLista[], ErrorApi>({
    queryKey: clavesListas.recientes,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ imports: ImportLista[] }>("/listas/import", {
        params: { limite: 10 },
        signal,
      });
      return r.imports;
    },
  });
}

/**
 * La vista de revision. Solo se pide cuando hay algo que revisar.
 *
 * Se trae entera (limite alto) y no paginada: el orden que importa —lo mas
 * peligroso arriba— se calcula sobre TODAS las filas. Paginar del lado del
 * servidor por `orden` dejaria la variacion del 90% en la pagina 7.
 */
export function useRevisionLista(
  id: string | null,
  habilitada: boolean,
): UseQueryResult<RespuestaRevision, ErrorApi> {
  return useQuery<RespuestaRevision, ErrorApi>({
    queryKey: clavesListas.revision(id ?? ""),
    queryFn: ({ signal }) =>
      api.get<RespuestaRevision>(`/listas/import/${id ?? ""}/revision`, {
        params: { limite: 2000 },
        signal,
        timeout: TIMEOUT_LARGO,
      }),
    enabled: id !== null && habilitada,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Correccion
// ─────────────────────────────────────────────────────────────────────────────

export interface CambiosFila {
  producto_id?: number | null;
  accion?: AccionFila;
  /** El costo bien escrito cuando la lectura corrio un decimal (0150). */
  costo?: number;
}

/**
 * Corrige una fila de la revision.
 *
 * Cambiar solo la accion se parchea en la cache: son 400 filas y refetchear la
 * lista entera por cada click haria que la tabla se reordene bajo el mouse.
 * Cambiar el PRODUCTO si obliga a refetchear, porque `v_lista_revision` calcula
 * `precio_resultante` con el markup del producto nuevo y ese numero no se puede
 * adivinar desde el frontend sin reimplementar `calcular_precio()`.
 */
export function useActualizarFila(
  importId: string | null,
): UseMutationResult<{ fila: FilaRevision }, ErrorApi, { filaId: number; cambios: CambiosFila }> {
  const qc = useQueryClient();

  return useMutation<{ fila: FilaRevision }, ErrorApi, { filaId: number; cambios: CambiosFila }>({
    mutationFn: ({ filaId, cambios }) =>
      api.patch<{ fila: FilaRevision }>(
        `/listas/import/${importId ?? ""}/filas/${filaId}`,
        cambios,
      ),
    onSuccess: (respuesta, variables) => {
      if (importId === null) return;

      // El costo tambien cambia el precio que va a quedar: se refetchea.
      if (variables.cambios.producto_id !== undefined || variables.cambios.costo !== undefined) {
        void qc.invalidateQueries({ queryKey: clavesListas.revision(importId) });
        void qc.invalidateQueries({ queryKey: clavesListas.import(importId) });
        return;
      }

      qc.setQueryData<RespuestaRevision>(clavesListas.revision(importId), (viejo) => {
        if (!viejo) return viejo;
        return {
          ...viejo,
          filas: viejo.filas.map((f) =>
            f.id === variables.filaId ? { ...f, ...respuesta.fila } : f,
          ),
        };
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Aplicar y revertir
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Lo que hay que tirar de la cache cuando se mueven costos.
 *
 * `aplicar_lista_import()` escribe `productos.costo` y el trigger de precios
 * recalcula `precio_venta`. Eso invalida el catalogo, la busqueda de precios y
 * el stock valorizado, aunque ninguna de esas pantallas este abierta ahora: la
 * cache sobrevive a la navegacion.
 */
function invalidarPorCostos(qc: ReturnType<typeof useQueryClient>, importId: string): void {
  for (const clave of [
    clavesListas.import(importId),
    clavesListas.revision(importId),
    claves.productos,
    claves.stock,
    claves.precios,
  ]) {
    void qc.invalidateQueries({ queryKey: [...clave] });
  }
}

export function useAplicarImport(
  importId: string | null,
): UseMutationResult<ResultadoAplicar, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<ResultadoAplicar, ErrorApi, void>({
    mutationFn: () =>
      api.post<ResultadoAplicar>(`/listas/import/${importId ?? ""}/aplicar`, undefined, {
        timeout: TIMEOUT_LARGO,
      }),
    onSuccess: () => {
      if (importId !== null) invalidarPorCostos(qc, importId);
    },
  });
}

export function useRevertirImport(
  importId: string | null,
): UseMutationResult<ResultadoRevertir, ErrorApi, void> {
  const qc = useQueryClient();
  return useMutation<ResultadoRevertir, ErrorApi, void>({
    mutationFn: () =>
      api.post<ResultadoRevertir>(`/listas/import/${importId ?? ""}/revertir`, undefined, {
        timeout: TIMEOUT_LARGO,
      }),
    onSuccess: () => {
      if (importId !== null) invalidarPorCostos(qc, importId);
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Memoria del ultimo import
// ─────────────────────────────────────────────────────────────────────────────

const CLAVE_MEMORIA = "tora.listas.import";

/**
 * El id del ultimo import queda guardado en el navegador.
 *
 * No es una comodidad: el backend no expone un listado de imports, asi que si el
 * id se pierde (F5, cerrar la pestana, el celular que descarta la pagina) el
 * boton de REVERTIR se vuelve inalcanzable. La red de seguridad no puede
 * depender de que nadie recargue.
 */
export function recordarImport(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(CLAVE_MEMORIA);
    else localStorage.setItem(CLAVE_MEMORIA, id);
  } catch {
    // Modo privado o storage lleno: se pierde la memoria, no la pantalla.
  }
}

export function importRecordado(): string | null {
  try {
    const guardado = localStorage.getItem(CLAVE_MEMORIA);
    return guardado === null || guardado === "" ? null : guardado;
  } catch {
    return null;
  }
}

/** Varias decisiones de una (0150): «Sí, son estos» confirma todos los parecidos. */
export function useActualizarFilas(
  importId: string | null,
): UseMutationResult<{ actualizadas: number }, ErrorApi, Array<{ id: number } & CambiosFila>> {
  const qc = useQueryClient();
  return useMutation<{ actualizadas: number }, ErrorApi, Array<{ id: number } & CambiosFila>>({
    mutationFn: (filas) =>
      api.patch<{ actualizadas: number }>(`/listas/import/${importId ?? ""}/filas`, { filas }),
    onSuccess: () => {
      if (importId === null) return;
      void qc.invalidateQueries({ queryKey: clavesListas.revision(importId) });
      void qc.invalidateQueries({ queryKey: clavesListas.import(importId) });
    },
  });
}

/** El proveedor de la lista, elegido o corregido despues de leerla (0150). */
export function useCambiarProveedorLista(
  importId: string | null,
): UseMutationResult<unknown, ErrorApi, number | null> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, number | null>({
    mutationFn: (proveedorId) =>
      api.patch(`/listas/import/${importId ?? ""}`, { proveedor_id: proveedorId }),
    onSuccess: () => {
      if (importId === null) return;
      void qc.invalidateQueries({ queryKey: clavesListas.revision(importId) });
      void qc.invalidateQueries({ queryKey: clavesListas.import(importId) });
      void qc.invalidateQueries({ queryKey: clavesListas.recientes });
    },
  });
}
